import axios from 'axios';
import { extractApiMessage } from '../utils/api-error';

const urls = {
  me: 'user/me',
  login: 'user/login',
  logout: 'user/logout',
  changePassword: 'user/change-password',
  resetPassword: 'user/reset-password',
  changeInfo: 'user/change-info',
  registerAccount: 'user/register',
  inviteUser: 'user/invite-new',
  listUsers: 'user/list-users',
  assignEmployee: 'user/assign-employee',
  favoriteEmployee: 'user/favorite-employee',
  delete: 'user',
  changeInfoAndRole: 'user/change-info-and-role',
  assignProject: 'user/assign-project',
  fetchProjectRoles: 'user/project-roles',
};

export default class UserService {
  async userIsLoggedIn() {
    const { data } = await axios({
      method: 'get',
      withCredentials: true,
      url: urls.me,
    });
    return data;
  }

  async login(email, password) {
    const { data } = await axios({
      method: 'post',
      url: urls.login,
      withCredentials: true,
      data: {
        email,
        password,
      },
    });
    return data;
  }

  async logout() {
    try {
      return await axios({
        method: 'post',
        withCredentials: true,
        url: urls.logout,
      });
    } catch (error) {
      console.log('logout', error);
      throw extractApiMessage(error);
    }
  }

  async changePassword(email, oldPassword, newPassword) {
    try {
      const { data } = await axios({
        method: 'post',
        url: urls.changePassword,
        withCredentials: true,
        data: {
          email,
          oldPassword,
          newPassword,
        },
      });
      return data;
    } catch (error) {
      console.log('changePassword', error);
      throw extractApiMessage(error);
    }
  }

  async generatePasswordToken(email) {
    try {
      const { data } = await axios({
        method: 'post',
        url: urls.resetPassword,
        withCredentials: true,
        data: {
          email,
        },
      });
      return data;
    } catch (error) {
      console.log('generatePasswordToken', error);
      throw extractApiMessage(error);
    }
  }

  async resetPassword(token, password) {
    try {
      const { data } = await axios({
        method: 'post',
        withCredentials: true,
        url: `${urls.resetPassword}/${token}`,
        data: {
          password,
        },
      });
      return data;
    } catch (error) {
      console.log('resetPassword', error);
      throw extractApiMessage(error);
    }
  }

  async changeProfileInfo(oldEmail, newEmail, name, locale) {
    try {
      const { data } = await axios({
        method: 'put',
        withCredentials: true,
        url: urls.changeInfo,
        data: {
          old_email: oldEmail,
          new_email: newEmail,
          full_name: name,
          locale,
        },
      });
      return data;
    } catch (error) {
      console.log('changeProfileInfo', error);
      throw extractApiMessage(error);
    }
  }

  async registerAccount(full_name, email, password, token, company_name, locale) {
    try {
      const { data } = await axios({
        method: 'post',
        withCredentials: true,
        url: urls.registerAccount,
        data: {
          email,
          password,
          token,
          full_name,
          company_name,
          locale,
        },
      });
      return data;
    } catch (error) {
      console.log('registerAccount', error);
      throw extractApiMessage(error);
    }
  }

  async inviteUser(email, company_id) {
    try {
      const { data } = await axios({
        method: 'post',
        withCredentials: true,
        url: `${urls.inviteUser}/${company_id}`,
        data: {
          email,
        },
      });
      return data;
    } catch (error) {
      console.log('inviteUser', error);
      throw extractApiMessage(error);
    }
  }

  async listUsers(companyId) {
    try {
      const { data } = await axios({
        method: 'get',
        withCredentials: true,
        url: `${urls.listUsers}/${companyId}`,
      });
      return data;
    } catch (e) {
      console.log('listUsers', e);
      throw extractApiMessage(e);
    }
  }

  async assignEmployeeToUser(email, companyId, projectId, employee_id) {
    try {
      await axios({
        method: 'post',
        withCredentials: true,
        url: `${urls.assignEmployee}/${companyId}/${projectId}`,
        data: {
          email,
          employee_id,
        },
      });
    } catch (e) {
      console.log('assignEmployeesToUser', e);
      throw extractApiMessage(e);
    }
  }

  async unassignEmployeeFromUser(email, companyId, projectId, employee_id) {
    try {
      await axios({
        method: 'delete',
        withCredentials: true,
        url: `${urls.assignEmployee}/${companyId}/${projectId}`,
        data: {
          email,
          employee_id,
        },
      });
    } catch (e) {
      console.log('unassignEmployeeFromUser', e);
      throw extractApiMessage(e);
    }
  }

  async setFavoriteEmployeeToUser(email, companyId, projectId, employee_id) {
    try {
      await axios({
        method: 'post',
        withCredentials: true,
        url: `${urls.favoriteEmployee}/${companyId}/${projectId}`,
        data: {
          email,
          employee_id,
        },
      });
    } catch (e) {
      console.log('setFavoriteEmployeeToUser', e);
      throw extractApiMessage(e);
    }
  }

  async removeFavoriteEmployeeFromUser(email, companyId, projectId) {
    try {
      await axios({
        method: 'delete',
        withCredentials: true,
        url: `${urls.favoriteEmployee}/${companyId}/${projectId}`,
        data: {
          email,
        },
      });
    } catch (e) {
      console.log('removeFavoriteEmployeeFromUser', e);
      throw extractApiMessage(e);
    }
  }

  async removeUserFromCompany(companyId, email) {
    try {
      await axios({
        method: 'delete',
        withCredentials: true,
        url: `${urls.delete}/${companyId}/${email}`,
      });
    } catch (e) {
      console.log('removeUserFromCompany', e);
      throw extractApiMessage(e);
    }
  }

  async updateUserInfo(companyId, oldEmail, newName, newEmail, newRole) {
    try {
      await axios({
        method: 'post',
        withCredentials: true,
        url: `${urls.changeInfoAndRole}/${companyId}/${oldEmail}`,
        data: {
          new_name: newName,
          new_email: newEmail,
          new_role: newRole,
        },
      });
    } catch (e) {
      console.log('updateUserInfo', e);
      throw extractApiMessage(e);
    }
  }

  async assignProject(companyId, email, project_id, project_role) {
    try {
      await axios({
        method: 'post',
        withCredentials: true,
        url: `${urls.assignProject}/${companyId}/${email}`,
        data: {
          project_id,
          project_role,
        },
      });
    } catch (e) {
      console.log('assignProject', e);
      throw extractApiMessage(e);
    }
  }

  async unassignProject(companyId, email, project_id) {
    try {
      await axios({
        method: 'delete',
        withCredentials: true,
        url: `${urls.assignProject}/${companyId}/${email}`,
        data: {
          project_id,
        },
      });
    } catch (e) {
      console.log('unassignProject', e);
      throw extractApiMessage(e);
    }
  }

  async fetchProjectRoles(companyId) {
    try {
      const { data } = await axios({
        method: 'get',
        withCredentials: true,
        url: `${urls.fetchProjectRoles}/${companyId}`,
      });
      return data;
    } catch (e) {
      console.log('fetchProjectRoles', e);
      throw extractApiMessage(e);
    }
  }
}
